/**
 * Read-only camera signals from the input layer (camera follow return, render/camera.ts).
 *
 * Fed each frame by `syncCameraSignals` from the controls layer's `localControl` (input/controls.ts):
 *  - `moveWord`: held movement inputs as FOLLOW_BITS (forward / back / strafe / turn / autorun);
 *  - `lookHeld`: a camera drag or mouselook button is held;
 *  - `fedByInput`: set true if some other code writes these itself (syncCameraSignals then skips).
 * The camera only reads it. Mutable module state (one session at a time), reset by `resetCameraSignals`.
 */
import { FOLLOW_BITS, type FollowSignals } from '../render/camera';
import type { MoveKey } from './bindings';
import { localControl } from './controls';

export const cameraSignals: FollowSignals & { fedByInput: boolean } = { moveWord: 0, lookHeld: false, fedByInput: false };

export function resetCameraSignals(): void {
  cameraSignals.moveWord = 0;
  cameraSignals.lookHeld = false;
}

/** Fallback word from the camera-relative move keys (W/S = forward/back, A/D = strafe). */
export function moveWordFromKeys(keys: ReadonlySet<MoveKey>): number {
  let w = 0;
  if (keys.has('forward')) w |= FOLLOW_BITS.forward;
  if (keys.has('back')) w |= FOLLOW_BITS.back;
  if (keys.has('left')) w |= FOLLOW_BITS.strafeLeft;
  if (keys.has('right')) w |= FOLLOW_BITS.strafeRight;
  return w;
}

/**
 * Per frame (session): refresh the signals from the controls layer's `localControl` (unless a
 * feeder set `fedByInput`). A movement / turn / autorun change is an edge for the follow return;
 * WoW mouselook (`steering`) and any held mouse button (`dragging`) count as "look held".
 */
export function syncCameraSignals(held: ReadonlySet<MoveKey>): void {
  if (cameraSignals.fedByInput) return;
  let w = moveWordFromKeys(held);
  // Camera-relative: any move faces the run, so it counts as forward. Action / WoW controls: only
  // the forward axis (W, autorun, both buttons) does; a strafe / backpedal keeps the camera put.
  if (localControl.scheme === 'camera' ? localControl.moving : localControl.forward) w |= FOLLOW_BITS.forward;
  if (localControl.turning) w |= FOLLOW_BITS.turnLeft;
  if (localControl.autorun) w |= FOLLOW_BITS.autorun;
  cameraSignals.moveWord = w;
  cameraSignals.lookHeld = localControl.steering || localControl.dragging;
}

/** The facing the camera returns behind: the client-steered facing when there is one. */
export function followFacing(unitFacing: number): number {
  return localControl.facing ?? unitFacing;
}
